import { useCallback, useEffect, useState } from 'react';

import { socialApi } from '@/api';

import { PageHeader } from '@/components/layout/PageHeader';

import { Button } from '@/components/ui/Button';

import { Input } from '@/components/ui/Input';

import { Select } from '@/components/ui/Select';

import type {

  SocialBrand,

  SocialCompetitor,

  SocialIntegrationConfig,

  SocialListeningKeyword,

} from '@/types/social';

import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';
const INTEGRATION_PLATFORMS = [

  { value: 'FACEBOOK', label: 'Meta (Facebook / Instagram)' },

  { value: 'LINKEDIN', label: 'LinkedIn' },

  { value: 'TWITTER', label: 'X (Twitter)' },

  { value: 'YOUTUBE', label: 'YouTube (Google OAuth)' },

  { value: 'GOOGLE_BUSINESS', label: 'Google Business' },

];



const COMPETITOR_PLATFORMS = [

  { value: 'FACEBOOK', label: 'Facebook' },

  { value: 'INSTAGRAM', label: 'Instagram' },

  { value: 'LINKEDIN', label: 'LinkedIn' },

  { value: 'TWITTER', label: 'X (Twitter)' },

];



export function SocialSetupPage({
  embedded,
  tab,
}: { embedded?: boolean; tab?: 'integrations' | 'brands' } = {}) {

  const [brands, setBrands] = useState<SocialBrand[]>([]);

  const [keywords, setKeywords] = useState<SocialListeningKeyword[]>([]);

  const [competitors, setCompetitors] = useState<SocialCompetitor[]>([]);

  const [integrations, setIntegrations] = useState<SocialIntegrationConfig[]>([]);

  const [brandName, setBrandName] = useState('');

  const [keyword, setKeyword] = useState('');

  const [competitorName, setCompetitorName] = useState('');

  const [competitorPlatform, setCompetitorPlatform] = useState('FACEBOOK');

  const [integrationPlatform, setIntegrationPlatform] = useState('FACEBOOK');

  const [clientId, setClientId] = useState('');

  const [clientSecret, setClientSecret] = useState('');

  const [webhookToken, setWebhookToken] = useState('');

  const [savingIntegration, setSavingIntegration] = useState(false);



  const load = useCallback(async () => {

    try {

      const [b, k, c, i] = await Promise.all([

        socialApi.listBrands().catch(() => []),

        socialApi.listListeningKeywords().catch(() => []),

        socialApi.listCompetitors().catch(() => []),

        socialApi.listIntegrationConfigs().catch(() => []),

      ]);

      setBrands(b);

      setKeywords(k);

      setCompetitors(c);

      setIntegrations(i);

    } catch (err) {

      showApiError(err, 'Failed to load setup');

    }

  }, []);



  useEffect(() => {

    load();

  }, [load]);



  const createBrand = async () => {

    if (!brandName.trim()) return;

    try {

      await socialApi.createBrand({ name: brandName });

      notify.success('Brand created');

      setBrandName('');

      load();

    } catch (err) {

      showApiError(err, 'Failed to create brand');

    }

  };



  const addKeyword = async () => {

    if (!keyword.trim()) return;

    try {

      await socialApi.addListeningKeyword({ keywordValue: keyword, keywordType: 'KEYWORD' });

      notify.success('Keyword added');

      setKeyword('');

      load();

    } catch (err) {

      showApiError(err, 'Failed to add keyword');

    }

  };



  const addCompetitor = async () => {

    if (!competitorName.trim()) return;

    try {

      await socialApi.addCompetitor({ platform: competitorPlatform, profileName: competitorName });

      notify.success('Competitor added');

      setCompetitorName('');

      load();

    } catch (err) {

      showApiError(err, 'Failed to add competitor');

    }

  };



  const saveIntegration = async () => {

    setSavingIntegration(true);

    try {

      await socialApi.saveIntegrationConfig({

        platform: integrationPlatform,

        clientId: clientId || undefined,

        clientSecret: clientSecret || undefined,

        webhookVerifyToken: webhookToken || undefined,

        apiVersion: 'v19.0',

      });

      notify.success('Integration credentials saved');

      setClientSecret('');

      load();

    } catch (err) {

      showApiError(err, 'Failed to save integration');

    } finally {

      setSavingIntegration(false);

    }

  };



  const configuredFor = (platform: string) =>

    integrations.find((i) => i.platform === platform)?.configured ?? false;



  const showIntegrations = !tab || tab === 'integrations';
  const showBrands = !tab || tab === 'brands';

  return (

    <div>

      {!embedded && (

      <PageHeader

        title="Social Setup"

        description="Platform credentials, multi-brand, listening, and competitor tracking"

        breadcrumbs={[{ label: 'Social', to: '/social' }, { label: 'Setup' }]}

      />

      )}

      <div className="grid gap-6 lg:grid-cols-2">

        {showIntegrations && (

        <div className="card p-4 lg:col-span-2">

          <h3 className="mb-1 text-sm font-semibold">Platform integration (live OAuth)</h3>

          <p className="mb-4 text-xs text-slate-500">

            Leave blank to use demo mode — channels still connect and publish with simulated provider responses.

            Add Meta or LinkedIn app credentials here for real OAuth and webhooks.

          </p>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">

            <Select

              label="Platform"

              value={integrationPlatform}

              onChange={setIntegrationPlatform}

              options={INTEGRATION_PLATFORMS}

            />

            <Input label="App ID / Client ID" value={clientId} onChange={(e) => setClientId(e.target.value)} />

            <Input

              label="App secret"

              type="password"

              value={clientSecret}

              onChange={(e) => setClientSecret(e.target.value)}

              placeholder={configuredFor(integrationPlatform) ? '••••••••' : ''}

            />

            <Input

              label="Webhook verify token"

              value={webhookToken}

              onChange={(e) => setWebhookToken(e.target.value)}

            />

          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">

            <Button onClick={saveIntegration} loading={savingIntegration}>Save credentials</Button>

            <span className="text-xs text-slate-500">

              Status:{' '}

              {INTEGRATION_PLATFORMS.map((p) => (

                <span key={p.value} className="mr-2">

                  {p.label.split(' ')[0]} {configuredFor(p.value) ? '✓' : 'demo'}

                </span>

              ))}

            </span>

          </div>

        </div>

        )}



        {showBrands && (

        <>

        <div className="card p-4">

          <h3 className="mb-3 text-sm font-semibold">Brands</h3>

          <div className="mb-3 flex gap-2">

            <Input className="flex-1" placeholder="Brand name" value={brandName} onChange={(e) => setBrandName(e.target.value)} />

            <Button onClick={createBrand}>Add</Button>

          </div>

          <ul className="space-y-1 text-sm">

            {brands.map((b) => (

              <li key={b.id}>{b.name} · {b.timezone ?? 'UTC'}</li>

            ))}

            {brands.length === 0 && <li className="text-slate-500">No brands (Enterprise multi-brand)</li>}

          </ul>

        </div>



        <div className="card p-4">

          <h3 className="mb-3 text-sm font-semibold">Listening keywords</h3>

          <div className="mb-3 flex gap-2">

            <Input className="flex-1" placeholder="Keyword or #hashtag" value={keyword} onChange={(e) => setKeyword(e.target.value)} />

            <Button onClick={addKeyword}>Add</Button>

          </div>

          <ul className="space-y-1 text-sm">

            {keywords.map((k) => (

              <li key={k.id}>{k.keywordType}: {k.keywordValue}</li>

            ))}

            {keywords.length === 0 && <li className="text-slate-500">No keywords configured</li>}

          </ul>

        </div>



        <div className="card p-4 lg:col-span-2">

          <h3 className="mb-3 text-sm font-semibold">Competitors</h3>

          <div className="mb-3 flex flex-wrap gap-2">

            <Select

              label="Platform"

              value={competitorPlatform}

              onChange={setCompetitorPlatform}

              options={COMPETITOR_PLATFORMS}

            />

            <Input

              label="Profile name"

              value={competitorName}

              onChange={(e) => setCompetitorName(e.target.value)}

            />

            <div className="flex items-end">

              <Button onClick={addCompetitor}>Track competitor</Button>

            </div>

          </div>

          <ul className="grid gap-2 text-sm sm:grid-cols-2">

            {competitors.map((c) => (

              <li key={c.id} className="rounded border border-slate-200 p-2 dark:border-slate-700">

                {c.profileName} · {c.platform} · {c.followers ?? 0} followers

              </li>

            ))}

            {competitors.length === 0 && <li className="text-slate-500">No competitors tracked</li>}

          </ul>

        </div>

        </>

        )}

      </div>

    </div>

  );

}


